import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ClientStorage,
    ClientStorageState,
    DateFilters,
    WALLET_DISPLAY_MODE,
    WidgetLayout,
    WIDGETS,
} from './clientStorageTypes';
import { endOfDay, startOfDay } from 'date-fns';
import { DATE_PRESET_LABELS } from '@/state/legs/legsTypes';

export const initialSymbolsEmpty = [];
export const initialAdvancedFiltersEmpty = {};
export const initialWidgetsLayout = [
    {
        id: WIDGETS.WALLET_COHORTS_BY_PERP_EQUITY,
        label: WIDGETS.WALLET_COHORTS_BY_PERP_EQUITY,
        position: 0,
        isHidden: false,
        innerSettings: [
            {
                id: 'segments_labels.shrimp',
                label: 'segments_labels.shrimp',
                isHidden: false,
                position: 0,
            },
            {
                id: 'segments_labels.fish',
                label: 'segments_labels.fish',
                isHidden: false,
                position: 1,
            },
            {
                id: 'segments_labels.dolphin',
                label: 'segments_labels.dolphin',
                isHidden: false,
                position: 2,
            },
            {
                id: 'segments_labels.apex_predator',
                label: 'segments_labels.apex_predator',
                isHidden: false,
                position: 3,
            },
            {
                id: 'segments_labels.small_whale',
                label: 'segments_labels.small_whale',
                isHidden: false,
                position: 4,
            },
            {
                id: 'segments_labels.whale',
                label: 'segments_labels.whale',
                isHidden: false,
                position: 5,
            },
            {
                id: 'segments_labels.tidal_whale',
                label: 'segments_labels.tidal_whale',
                isHidden: false,
                position: 6,
            },
            {
                id: 'segments_labels.leviathan',
                label: 'segments_labels.leviathan',
                isHidden: false,
                position: 7,
            },
        ],
    },
    {
        id: WIDGETS.WALLET_COHORTS_BY_ALL_TIME_PERP_PNL,
        label: WIDGETS.WALLET_COHORTS_BY_ALL_TIME_PERP_PNL,
        position: 1,
        isHidden: false,
        innerSettings: [
            {
                id: 'segments_labels.giga_rekt',
                label: 'segments_labels.giga_rekt',
                isHidden: false,
                position: 0,
            },
            {
                id: 'segments_labels.full_rekt',
                label: 'segments_labels.full_rekt',
                isHidden: false,
                position: 1,
            },
            {
                id: 'segments_labels.semi_rekt',
                label: 'segments_labels.semi_rekt',
                isHidden: false,
                position: 2,
            },
            {
                id: 'segments_labels.exit_liquidity',
                label: 'segments_labels.exit_liquidity',
                isHidden: false,
                position: 3,
            },
            {
                id: 'segments_labels.humble_earner',
                label: 'segments_labels.humble_earner',
                isHidden: false,
                position: 4,
            },
            {
                id: 'segments_labels.consistent_grinder',
                label: 'segments_labels.consistent_grinder',
                isHidden: false,
                position: 5,
            },
            {
                id: 'segments_labels.smart_money',
                label: 'segments_labels.smart_money',
                isHidden: false,
                position: 6,
            },
            {
                id: 'segments_labels.money_printer',
                label: 'segments_labels.money_printer',
                isHidden: false,
                position: 7,
            },
        ],
    },
    {
        id: WIDGETS.TRENDING_WALLETS,
        label: WIDGETS.TRENDING_WALLETS,
        position: 2,
        isHidden: false,
    },
    {
        id: WIDGETS.POSITION_HEATMAP,
        label: WIDGETS.POSITION_HEATMAP,
        position: 3,
        isHidden: false,
    },
    {
        id: WIDGETS.LEADERBOARDS,
        label: WIDGETS.LEADERBOARDS,
        position: 4,
        isHidden: false,
    },
    {
        id: WIDGETS.STATS,
        label: WIDGETS.STATS,
        position: 5,
        isHidden: false,
        innerSettings: [
            {
                id: 'open_interest',
            },
            {
                id: 'daily_volume',
            },
        ],
    },
    {
        id: WIDGETS.FAVORITES,
        label: WIDGETS.FAVORITES,
        position: 6,
        isHidden: false,
    },
];

export const initialDate: DateFilters = {
    startDate: startOfDay(new Date('01/06/2016')).toISOString(),
    endDate: endOfDay(new Date('01/06/2016')).toISOString(),
    label: DATE_PRESET_LABELS.LAST_50_TRADES,
};

const initialClientStorageData: ClientStorage = {
    selectedAccounts: [],
    generalAdvancedFilters: {},
    accountAdvancedFilters: {},
    generalDateSelection: initialDate,
    accountDateSelection: {},
    generalSymbolSelection: [],
    accountSymbolSelection: {},
    widgetsLayout: initialWidgetsLayout,
    isLiquidationSoundMuted: false,
    walletDisplayMode: WALLET_DISPLAY_MODE.NAMES,
};

const initialState: ClientStorageState = {
    clientStorage: initialClientStorageData,
    currentAdvancedSelection: {},
    isLoading: false,
    innerSettingsChanged: '',
    selectedLeaderboardsTab: 0,
    selectedTrendingWalletsTab: 0,
    heatmapDisabledColumns: [],
};

const slice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setClientStorageState: (state, action: PayloadAction<Partial<ClientStorage>>) => {
            state.isLoading = false;
            state.clientStorage = { ...initialClientStorageData, ...action.payload };
        },
        setCurrentAdvancedSelection: (state, action: PayloadAction<Partial<ClientStorage>>) => {
            state.currentAdvancedSelection = action.payload;
        },
        setWidgetsLayout: (state, action: PayloadAction<WidgetLayout[]>) => {
            state.clientStorage.widgetsLayout = action.payload;
        },
        setInnerSettingsChanged: (state, action: PayloadAction<string>) => {
            state.innerSettingsChanged = action.payload;
        },
        setSelectedLeaderboardsTab: (state, action: PayloadAction<number>) => {
            state.selectedLeaderboardsTab = action.payload;
        },
        setSelectedTrendingWalletsTab: (state, action: PayloadAction<number>) => {
            state.selectedTrendingWalletsTab = action.payload;
        },
        setHeatmapDisabledColumns: (state, action: PayloadAction<number[]>) => {
            state.heatmapDisabledColumns = action.payload;
        },
        setWalletDisplayMode: (state, action: PayloadAction<WALLET_DISPLAY_MODE>) => {
            state.clientStorage.walletDisplayMode = action.payload;
        },
    },
});

export const {
    setClientStorageState,
    setCurrentAdvancedSelection,
    setIsLoading,
    setWidgetsLayout,
    setInnerSettingsChanged,
    setSelectedLeaderboardsTab,
    setHeatmapDisabledColumns,
    setSelectedTrendingWalletsTab,
    setWalletDisplayMode,
} = slice.actions;

export default slice.reducer;
